import { forwardRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { format } from 'date-fns';

interface QRData {
  workOrderId: string;
  workOrderNumber: string;
  processId: string;
  processName: string;
  operatorId: string;
  operatorName: string;
  timestamp: string;
  pieceNumber?: string;
  productName?: string;
}

interface PrintableLabelProps {
  data: QRData;
  companyName?: string;
}

export const PrintableLabel = forwardRef<HTMLDivElement, PrintableLabelProps>(
  ({ data, companyName = 'Madelmold MES' }, ref) => {
    const qrValue = JSON.stringify(data);

    return (
      <div ref={ref} className="print-label">
        {/* This content will be formatted for printing */}
        <style>
          {`
            @media print {
              @page {
                size: 100mm 50mm;
                margin: 0;
              }
              body {
                margin: 0;
                padding: 0;
              }
              .print-label {
                width: 100mm;
                height: 50mm;
                padding: 5mm;
                display: flex;
                gap: 3mm;
                font-family: Arial, sans-serif;
                page-break-after: always;
              }
              .no-print {
                display: none !important;
              }
            }
          `}
        </style>
        
        <div style={{
          width: '100mm',
          height: '50mm',
          padding: '5mm',
          display: 'flex',
          gap: '3mm',
          border: '1px solid #e5e7eb',
          fontFamily: 'Arial, sans-serif',
          backgroundColor: 'white'
        }}>
          {/* QR Code Section */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40mm'
          }}>
            <QRCodeSVG
              value={qrValue}
              size={150}
              level="H"
              includeMargin={false}
            />
          </div>

          {/* Information Section */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            fontSize: '9pt'
          }}>
            {/* Header */}
            <div style={{ borderBottom: '2px solid #000', paddingBottom: '2mm' }}>
              <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                {companyName}
              </div>
              <div style={{ fontSize: '12pt', fontWeight: 'bold', marginTop: '1mm' }}>
                {data.workOrderNumber}
              </div>
            </div>

            {/* Body */}
            <div style={{ flex: 1, paddingTop: '2mm', paddingBottom: '2mm' }}>
              {data.productName && (
                <div style={{ fontSize: '8pt', marginBottom: '1mm' }}>
                  <strong>Producto:</strong> {data.productName}
                </div>
              )}
              <div style={{ fontSize: '8pt', marginBottom: '1mm' }}>
                <strong>Proceso:</strong> {data.processName}
              </div>
              <div style={{ fontSize: '8pt', marginBottom: '1mm' }}>
                <strong>Operario:</strong> {data.operatorName}
              </div>
              {data.pieceNumber && (
                <div style={{ fontSize: '10pt', fontWeight: 'bold', marginTop: '1mm' }}>
                  Pieza: {data.pieceNumber}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ 
              borderTop: '1px solid #666', 
              paddingTop: '2mm',
              fontSize: '7pt',
              color: '#666'
            }}>
              {format(new Date(data.timestamp), 'dd/MM/yyyy HH:mm')}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

PrintableLabel.displayName = 'PrintableLabel';
